<template>
    <div class="">
        <b-input-group v-if="editMode" size="sm">
            <b-form-select 
                :value="localName"
                @input="handleFieldInput('localName', $event)"
                placeholder="Office/Mobile"
                :options="nameSuggestions" 
                value-field="value"
                text-field="label"
                :state="localNameIsValid ? null : hasAnyRequiredInput ? false : null"
                :class="!localName ? 'text-muted' : ''"
                style="max-width: 100px;"
            ></b-form-select>
            <b-form-select
                :value="localCountry"
                @input="handleFieldInput('localCountry', $event)"
                :options="localCountryList"
                value-field="code"
                text-field="name"
                :state="localCountryIsValid ? null : hasAnyRequiredInput ? false : null"
                class="w-35"
                :class="!localCountry ? 'text-muted' : ''"
            ></b-form-select>
            <b-form-input 
                type="tel" 
                aria-label="Phone Number" 
                :value="localNumberInput"
                @input="handleFieldInput('localNumberInput', $event)"
                placeholder="Phone number..."
                required
                trim
                :state="localNumberIsValid ? null : hasAnyRequiredInput ? false : null"
                class="w-30"
            ></b-form-input>
            <b-form-input 
                :value="localExtension" 
                @input="handleFieldInput('localExtension', $event)"
                aria-label="Extension"
                placeholder="ext."
                style="max-width: 70px;"
            ></b-form-input>
            <b-input-group-append>
                <b-input-group-text :class="allThingsValid ? '' : 'bg-danger-50'">
                    <b-icon :variant="allThingsValid ? 'success' : 'danger'" :icon="allThingsValid ? 'check2-circle' : 'exclamation-circle'" />
                </b-input-group-text>
                <b-button v-if="!noRemove" variant="sharp-danger" @click="emitRemove">
                    <b-icon variant="danger" icon="trash" />
                </b-button>
            </b-input-group-append>
        </b-input-group>
        <div
            v-else
            class="d-flex gap-2 align-items-center"
        >
            <country-flag v-if="localCountry" :country="localCountry" size="sm"></country-flag>
            <div class="d-flex align-items-center gap-2">
                <div class="font-weight-500">
                    <!-- {{ numberDisplayInternationalFormatted }} -->
                    <!-- RFC3966 compliant link to dial the number - hold for now -->
                    <a :href="telLink" class="spl-phone-number-link">{{ numberDisplayInternationalFormatted }}</a>
                </div>
                <div v-if="localExtension" class="">ext. {{ localExtension }}</div>
            </div>
            <div class="special-text text-muted font-weight-400" style="font-size: 0.8em;">{{ localName }}</div>
        </div>
    </div>
</template>

<script>
import { AsYouType } from 'libphonenumber-js/min';

export default {
    name: 'BootstrapPhoneNumberInput',
    components: {
    },
    mixins: [],
    props: {
        phoneData: {
            type: Object,
            default: () => {
                return {
                    name: '',
                    country: '',  // just the code
                    number: '',  // SHOULD ALWAYS BE IN E.164 FORMAT (with the +1....) - we emit that back up also
                    extension: '',
                };
            },
        },
        // should have { code, name }
        countries: {
            type: Array,
            default: () => [],
        },
        // just the code, if there is a preferred one 
        preferredCountryCode: {
            type: String,
            default: '',
        },
        editMode: {  // TODO
            type: Boolean,
            default: false,
        },
        noRemove: {  // if true, don't show/allow the remove button
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            useStrictValidation: false,  // true means use .isValid() for strict checking, false means use .isPossible() for more lenient checking (libphonenumber-js author actually recommends this for most projects unless keeping super up to date)
            // DATA THAT WE PASS BACK UP:
            localName: '',
            localCountry: this.preferredCountryCode,  // just the code, not the name
            localExtension: '',
            localNumberE164: '',  // ALWAYS IN E.164 FORMAT (with the +1....) - we emit that back up also
            // DATA THAT WE USE LOCALLY:
            localNumberInput: '',  // this is the thing in the input field - gets set to formatted number as you type, it might be getting cleared out right away too if not valid!
            numberDisplayInternationalFormatted: '',  // with the country code, but also formatted, for display in non-edit mode
            numberDisplayNationalFormatted: '',  // for possible display, non-edit usage
            numberDisplayNational: '',  // for possible display, non-edit usage
            // VALIDATION FLAGS:
            localNameIsValid: false,
            localCountryIsValid: false,
            localNumberIsValid: false,
            // localExtensionIsValid: false,  // not using this for now.... not sure what the rules should be
            // OPTIONS FOR NAME:
            nameSuggestions: [
                // keep value and label the same for now - this will just be a text input in the backend
                {value: '', label: '-Select-', disabled: true},
                {value: 'Mobile', label: 'Mobile', disabled: false},
                {value: 'Office', label: 'Office', disabled: false},
                {value: 'Personal', label: 'Personal', disabled: false},
                {value: 'Other', label: 'Other', disabled: false},
            ],
        }
    },
    // watch: {
    //     phoneData: {
    //         // watch for changes in the phoneData prop from parent
    //         handler: function(newVal) {
    //             // console.log('phone number watcher - new, old:', newVal, oldVal);
    //             // TODO - if we have invalid data locally (therefore out of sync with parent), but something happens in parent that triggers this watcher, we should ignore it
    //             // should we just always ignore it, only set initial local values from parent, then update parent if/when we have something new valid?
    //             // that is basically necessary if you no longer report invalid things up to parent (which you changed in the emit update method)
    //             this.localName = newVal.name || '';
    //             this.localCountry = newVal.country || this.preferredCountryCode || '';
    //             this.localExtension = newVal.extension || '';
    //             // for number stuff, we need to convert to correct formats
    //             const e164Number = newVal.number || '';  // will be the E.164 formatted number:  '+14084218888'
    //             this.setLocalNumberDataFromParent(e164Number);
    //             console.log('about to call afterAnyInput from phoneData watcher');
    //             this.afterAnyInput();  // gotta call this to check for changes and emit if needed (to emit validation changes too)
    //         },
    //         deep: true,
    //         immediate: true,  // also run on created
    //     },
    // },
    computed: {
        allThingsValid() {
            // NOTE: only use this for things in the template, not things 'in the loop' of checking validity from methods
            //       because it might not be updated in time for the method to use it
            return this.localNameIsValid && this.localCountryIsValid && this.localNumberIsValid;
        },
        telLink() {
            // returns the tel link for the number, with extension if present, RFC3966 compliant
            return `tel:${this.localNumberE164}${this.localExtension ? `;${this.localExtension}` : ''}`;
        },
        hasAnyRequiredInput() {
            // we track if any of the required inputs is entered,
            // and we only show the validation state if there is any input in any of those
            // so we don't show validation state if nothing is entered (all required fields are empty) 
            return (!!this.localNumberInput && this.localNumberInput.trim().length > 0) || !!this.localCountry || !!this.localName;
        },
        localCountryList() {
            // return the countries prop, but with something for the '' empty option
            const blankOption = { code: '', name: '-Select Country -', disabled: true };
            // we might have to nest that in another group if we have nested options? {label: 'label', options: []}
            return [blankOption, ...this.countries];
        },
    },
    created() {
        // PROBLEM -- we probably DO need a watcher on this... because parent form (in workflows, for example) might save data, then it gets passed back here and its different... it will get ignored....
        this.setInitialDataFromParent(this.phoneData);  // set initial data from parent, if provided
    },
    mounted() {
        
    },
    methods: {
        // PUBLIC METHODS - these are the ones that are called from the parent
        isValid() {
            return this.localNameIsValid && this.localCountryIsValid && this.localNumberIsValid;
        },
        setInitialDataFromParent(phoneData) {
            if (!phoneData || typeof phoneData !== 'object') {
                console.warn('setInitialDataFromParent called with invalid phoneData:', phoneData);
                return;
            }
            // this is called from parent to set initial data, if needed -- basically the same code we had in watcher, but we got rid of watcher, only do this for initial data
            // phoneData should be an object with name, country, number, extension
            this.localName = phoneData.name || '';
            this.localCountry = phoneData.country || this.preferredCountryCode || '';
            this.localExtension = phoneData.extension || '';
            // for number stuff, we need to convert to correct formats
            const e164Number = phoneData.number || '';  // will be the E.164 formatted number:  '+14084218888'
            this.setLocalNumberDataFromParent(e164Number);
            // console.log('about to call afterAnyInput from setInitialDataFromParent method');
            this.afterAnyInput();  // gotta call this to check for changes and emit if needed (to emit validation changes too)
        },
        // HANDLING THINGS FROM PARENT
        setLocalNumberDataFromParent(rawNumber) {
            const numberData = this.parseRawNumber(rawNumber);
            //console.table(numberData);
            const { national, nationalFormatted, internationalFormatted, e164, isValid } = numberData;
            this.localNumberInput = nationalFormatted;  // we do NOT use asYouTypeFormatted for setting the input field from parent, because it will always have the +country code, and we don't want that in the input, only national numbers in the input
            this.numberDisplayNationalFormatted = nationalFormatted;
            this.numberDisplayNational = national;
            this.numberDisplayInternationalFormatted = internationalFormatted;
            this.localNumberE164 = e164;
            this.localNumberIsValid = isValid;
            this.localNameIsValid = this.localName.length > 0;
            this.localCountryIsValid = this.localCountry.length > 0;
        },
        // INTERNAL METHODS
        handleFieldInput(fieldName, value) {
            // use this as part of v-model loop for all fields - hook into @input or @change???
            // first set the local value, then call the afterAnyInput method -- to make sure things happen in that order (explicitly)
            const oldVal = this[fieldName];
            if (oldVal === value) {
                // console.log(`in handleFieldInput - No change for field: ${fieldName}, value: ${value}`);
                return;
            } else {
                this[fieldName] = value;
                // should we NOT do this for number field?  
                // console.log(`about to call afterAnyInput from handleFieldInput - field: ${fieldName}, value: ${value}`);
                this.afterAnyInput({forceEmit: true});
            }
        },
        afterAnyInput({forceEmit = false} = {}) {
            // pass in forceEmite = true if you want to emit even if nothing about the NUMBER has changed
            // need to do this if you change country, name, extension -- those may not affect the number parsing itself
            // console.log('starting afterAnyInput');
            // TODO - if user specifically entered a country code, and it doesn't match the selected country (because the preferred country code is not correct) then we should update the country based on country code
            //      - also, make preferredCountryCode reactive after initial create?  maybe no need... will get confusing... it gets set each time you add a new line anyways (new instance of this component)
            // console.log('in afterAnyInput - this.localNumberInput:', this.localNumberInput);
            const numberData = this.parseRawNumber(this.localNumberInput);
            // console.log('DUMPING DATA FROM afterAnyInput');
            // console.table(numberData);
            const { asYouTypeFormatted, national, nationalFormatted, internationalFormatted, e164, isValid } = numberData;
            // SET THE FOMATTED NUMBER IN THE INPUT FIELD
            // console.log('asYouTypeFormatted:', asYouTypeFormatted);
            this.localNumberInput = asYouTypeFormatted;  // you may? want to always set this to nationalFormatted?  if so, it will always chop off the +1 country code if someone types it
            // no need to set above to nationalFormatted... we have separate method for setting from parent
            // NEXT, check if anything we care about has changed, if so, update the local data and trigger emit to parent (once at the end) -- and update validation flags as we go
            let isNumberChanged = false;
            if (this.localNumberE164 !== e164) {
                this.localNumberE164 = e164;
                isNumberChanged = true;
            }
            // update display values -- but no need to set isNumberChanged for these
            if (this.numberDisplayNationalFormatted !== nationalFormatted) {
                this.numberDisplayNationalFormatted = nationalFormatted;
            }
            if (this.numberDisplayNational !== national) {
                this.numberDisplayNational = national;
            }
            if (this.numberDisplayInternationalFormatted !== internationalFormatted) {
                this.numberDisplayInternationalFormatted = internationalFormatted;
            }
            // set validation flags
            this.localNumberIsValid = isValid;  // do we need to check if this changed too???  maybe not, since it doesn't affect whether we emit or not.... but what if everything stayed same but only validation changed?  is that possible???  if so, then yes, need to check for changes here
            this.localNameIsValid = this.localName.length > 0;
            this.localCountryIsValid = this.localCountry.length > 0;
            // finally, did we change anything we care about?  if so, we must emit the change
            if (isNumberChanged || forceEmit) {
                this.handleAnyChange();
            } else {
                // console.log('No number changes, forceEmit is false, so not emitting');
            }
        },
        parseRawNumber(rawNumber) {
            // we SHOULD use AsYouType to format the number -- loooks better as you're typing, applies formatting as you type
            const asYouType = new AsYouType(this.localCountry);  // AsYouType() is a formatter instance... has bunch of class methods
            const formatted = asYouType.input(rawNumber);
            let phoneNumberObj = asYouType.getNumber();  // returns the PhoneNumber object, parsed from value as we type
            // let isValid = asYouTypePhoneNumberObj && (this.useStrictValidation ? asYouTypePhoneNumberObj.isValid() : asYouTypePhoneNumberObj.isPossible());  // use .isValid() for strict checking, or .isPossible() for more lenient checking (libphonenumber-js author actually recommends this for most projects unless keeping super up to date)
            // return {
            //     formatted: formatted,
            //     e164: isValid ? asYouTypePhoneNumberObj.number : '',
            //     isValid: isValid,
            // };
            //const phoneNumberObj = parsePhoneNumber(rawNumber, this.localCountry);
            if (phoneNumberObj) {
                return {
                    asYouTypeFormatted: formatted,  // will include if user enters +1 first (so don't use this for setting things from parent)
                    national: phoneNumberObj.nationalNumber,
                    nationalFormatted: phoneNumberObj.formatNational(),
                    internationalFormatted: phoneNumberObj.formatInternational(),
                    e164: phoneNumberObj.number,  // number is the E.164 formatted number:  '+14084218888'
                    isValid: this.useStrictValidation ? phoneNumberObj.isValid() : phoneNumberObj.isPossible(),  // use .isValid() for strict checking, or .isPossible() for more lenient checking (libphonenumber-js author actually recommends this for most projects unless keeping super up to date)
                };
            } else {
                return {
                    asYouTypeFormatted: formatted,
                    national: '',
                    nationalFormatted: '',
                    internationalFormatted: '',
                    e164: '',
                    isValid: false,
                }
            }
        },
        handleAnyChange() {  // we always check things from local here, not props
            const allThingsValid = this.isValid();  // no localName required?  
            // changed to always emit, but just set a flag to say if it's valid or not, then parent component will decide what to do with it
            // actually that kinda sucks.... this can have many parents.  and if we emit up, and parent sets our props to that, it will wipe out our local data
            // so we will split out validation emit and data emit
            if (allThingsValid) {
                // console.log('In handleAnyChange - all things valid, emitting update');
                const numberData = {
                    name: this.localName,
                    country: this.localCountry,
                    number: this.localNumberE164,
                    extension: this.localExtension,
                };
                // console.log('emitting update', numberData, allThingsValid);
                this.$emit('update', numberData, allThingsValid);
            }
            // then always emit validation change, so parent can react to it
            // console.log('emitting validation event', allThingsValid);
            this.$emit('validation', allThingsValid);
        },
        emitRemove() {
            if (this.noRemove) {
                console.warn('Remove button is disabled, not emitting remove event.');
                return;  // don't do anything if noRemove is true
            }
            this.$emit('remove');
        },
    },
}
</script>

<style lang="scss">

.spl-phone-number-link {
    text-decoration: none;
    color: inherit;  // inherit color from parent
    transition: color 0.1s ease-in-out;
    &:hover {
        text-decoration: none;
        color: var(--primary-700);  // use primary color on hover
    }
}

</style>